import * as React from 'react'
import * as Redux from 'react-redux'
import { styled, css } from 'next-yak'

import * as api from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import * as service from '@owl-nest/services'
import * as plume from '@ulule/owl-kit-components/next'

import * as UFE from '../../../../../UFE'
import * as projectModel from '../../../../models/project'
import { RootState } from '../../../../../reducers'
import { Share } from './Share'
import { ProjectFinanced } from './ProjectFinanced'
import { ProjectCancelled } from './ProjectCancelled'
import { ProjectFinancedOffboarding } from './ProjectFinancedOffboarding'
import { ProjectFinishedDonationBased } from './ProjectFinishedDonationBased'
import { ProjectNotFinanced } from './ProjectNotFinanced'
import { ContributionsBlock } from './ContributionsBlock'
import { DisplayMode } from '../../../../context'

export enum BlockStatus {
  SUCCESS_ACTIVE_ONBOARDING,
  SUCCESS_INACTIVE_ONBOARDING,
  FINISHED_DONATION_BASED,
  FINISHED_UNSUCCESSFULLY,
  CANCELLED,
  ONLINE,
  NONE,
}

type BackersBlockPropTypes = {
  status: BlockStatus
}

export function BackersBlock({ status }: BackersBlockPropTypes): React.ReactElement<BackersBlockPropTypes> {
  const [isOpen, setIsOpen] = React.useState(false)
  function toggle(): void {
    setIsOpen(!isOpen)
  }

  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const auth = service.user.useAuth()
  const isUserSupporter = Number(project.user_orders?.length) > 0

  const displayMode = React.useContext(DisplayMode)
  const isEditMode = displayMode === 'edit'

  return (
    <Main $isEditMode={isEditMode}>
      {status === BlockStatus.FINISHED_UNSUCCESSFULLY && isUserSupporter && (
        <Layout toggle={toggle} isOpen={isOpen} display={false}>
          <ProjectNotFinanced project={project} />
        </Layout>
      )}

      {status === BlockStatus.SUCCESS_ACTIVE_ONBOARDING && (
        <Layout toggle={toggle} isOpen={isOpen} display={isUserSupporter}>
          <ProjectFinancedOffboarding project={project} fullSize={!isUserSupporter} />
          {isUserSupporter && <ContributionsBlock isOpen={isOpen} project={project} />}
        </Layout>
      )}

      {status === BlockStatus.FINISHED_DONATION_BASED && (
        <Layout toggle={toggle} isOpen={isOpen} display={isUserSupporter}>
          <ProjectFinishedDonationBased project={project} fullSize={!isUserSupporter} />
          {isUserSupporter && <ContributionsBlock isOpen={isOpen} project={project} />}
        </Layout>
      )}

      {status === BlockStatus.SUCCESS_INACTIVE_ONBOARDING && (
        <Layout toggle={toggle} isOpen={isOpen} display={isUserSupporter}>
          <ProjectFinanced project={project} fullSize={!isUserSupporter} />
          {isUserSupporter && <ContributionsBlock isOpen={isOpen} project={project} />}
        </Layout>
      )}

      {status === BlockStatus.CANCELLED && (
        <Layout toggle={toggle} isOpen={isOpen} display={false}>
          <ProjectCancelled project={project} />
        </Layout>
      )}

      {status === BlockStatus.ONLINE && isUserSupporter && auth.type === 'loggedin' && (
        <>
          <Layout toggle={toggle} isOpen={isOpen} display={true}>
            <ContributionsBlock isOpen={isOpen} project={project} />
            {!plume.utils.isMobile() && <Share project={project} user={auth.user} />}
          </Layout>
          {plume.utils.isMobile() && <Share project={project} user={auth.user} />}
        </>
      )}
    </Main>
  )
}

type LayoutProps = {
  isOpen: boolean
  toggle: () => void
  display: boolean
  children: React.ReactNode
}

function Layout({ isOpen, toggle, display, children }: LayoutProps): React.ReactElement<LayoutProps> {
  return (
    <Wrapper>
      <ContentContainer>{children}</ContentContainer>
      <ShowDetails $isOpen={isOpen} $display={display} onClick={toggle} asDesktop="XS.regular">
        {isOpen ? t('Close') : t('See details')}
        <plume.glyphs.stroke.CaretDown size={12} />
      </ShowDetails>
    </Wrapper>
  )
}

export function useBlockStatus(): BlockStatus {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)

  const isProjectSuccess = model.project.success<projectModel.ExtraFields>(project)
  const isDonationBased = model.project.isDonationBased(project)
  const isFinished = model.project.isFinished<projectModel.ExtraFields>(project)
  const isCancelled = model.project.isCancelled<projectModel.ExtraFields>(project)
  const hasOnboarding = project.post_campaign_link !== undefined

  if (isCancelled) {
    return BlockStatus.CANCELLED
  }

  if (isDonationBased && isFinished) {
    return BlockStatus.FINISHED_DONATION_BASED
  }

  if (isProjectSuccess && hasOnboarding) {
    return BlockStatus.SUCCESS_ACTIVE_ONBOARDING
  }

  if (isProjectSuccess && !hasOnboarding) {
    return BlockStatus.SUCCESS_INACTIVE_ONBOARDING
  }

  if (model.project.isFinished<projectModel.ExtraFields>(project) && !project.goal_raised) {
    return BlockStatus.FINISHED_UNSUCCESSFULLY
  }

  if (project.status === api.ProjectStatus.ONLINE) {
    return BlockStatus.ONLINE
  }

  return BlockStatus.NONE
}

const Main = styled.div<{ $isEditMode: boolean }>`
  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
          padding-top: 20px;
        }
      `
    } else {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          padding-top: 20px;
        }
      `
    }
  }}
`

const Wrapper = styled.div`
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  max-width: 1200px;
  border-radius: 4px;
  box-sizing: border-box;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    box-shadow: ${plume.EFFECTS.SHADOW_5};
    margin: 20px auto 0px auto;
    background-image: ${() => `url('${UFE.IMAGES.sparkles}')`};
    background-size: contain;
    padding: 16px 56px;
  }
`

const ShowDetails = styled(plume.styles.copy.S.regular)<{ $isOpen: boolean; $display: boolean }>`
  text-align: center;
  padding: 6px 0;
  display: ${(props) => (props.$display ? 'block' : 'none')};

  ${plume.glyphs.stroke.CaretDown} {
    fill: ${plume.COLORS.PRIMARY_SAND_500};
    position: relative;
    margin-left: 5px;

    ${({ $isOpen }) => {
      if ($isOpen) {
        return css`
          top: 0;
          transform: rotateX(180deg);
        `
      }
    }}
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    display: none;
  }
`

const ContentContainer = styled.div`
  display: block;
  justify-content: center;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    display: flex;
  }
`
